import FJVideoElement from './FJVideoElement';
import FJSubtitle from './FJSubtitle';
import FJSubtitleText from './FJSubtitleText';
import FJCoreConfig from './FJCoreConfig';
import FJTextStyleManager from './textStyle/FJTextStyleManager';
import FJNetworkManager from './FJNetworkManager';
import FJConfig from '@src/config/FJConfig';
import FJExportStore from '@src/store/FJExportStore';
import FJUtil from '../util/FJUtil';
import FJMathUtil from '../util/FJMathUtil';
import FJDetectStore from '@src/store/FJDetectStore';
import FJFragmentDetectStore from '@src/store/FJFragmentDetectStore';
import FJSubtitleUtil from '@src/util/FJSubtitleUtil';
import FJEditAppUtil from '@src/util/FJEditAppUtil';
import FJVideoUtil from '@src/util/FJVideoUtil';
import FJArchiveUtil from '@src/util/FJArchiveUtil';
import FJFaceDetectScheduler from '@src/manager/FJFaceDetectScheduler';
import FJFontManager from './font/FJFontManager';
/** 字幕布局防抖（ms）：尺寸连续变化结束后再排版，避免与视频争用主线程 */
const SUBTITLE_LAYOUT_DEBOUNCE_MS = 140;

class FJDetailManager {
    /**
     * @param {import('./FJVideoPlayBase.js').default | null} [sharedVideoPlayBase] 由 `FJProjectStore` 对当前会话单次 `setSource` 后的共享实例；传 `null`/省略则每条仍自建 `FJVideoPlayBase`
     */
    constructor(sharedVideoPlayBase = null) {
        /** @private {import('./FJVideoPlayBase.js').default | null} */
        this._sharedVideoPlayBase = sharedVideoPlayBase;
        this._videoElement = new FJVideoElement();
        this._subtitle = new FJSubtitle();
        this._subtitleView = null; // 字幕预览挂载的 DOM 容器，与 video 同步控制
        this._duration = 0;
        this._currentTime = 0;
        this._isPlaying = false;
        this._exportRatio = null;
        this._subtitleStyleId = null;
        this._id = null;
        /** @type {{ width: number, height: number } | null} 用于判断字幕容器是否「跳变」，仅在跳变时 flush DOM，避免每次 resize 重建字幕 */
        this._lastSubtitleLayoutSize = null;
        /** @type {ReturnType<typeof setTimeout> | null} */
        this._subtitleResizeTimer = null;
        /** 当前挂载下是否已做过至少一次字幕布局（首帧尽快 rAF 同步，之后走防抖） */
        this._subtitleHadLayoutOnce = false;
        this._stableFragments = [];
        this._videoFps = 30;
        this._exportBlob = null;
    }
    async init({
        videoUrl,
        startTime,
        endTime,
        subtitles,
        subtitleStyleId,
        exportRatio,
        faceData,
        id,
        language,
        isLightVideo,
        stableFragments = [],
        fps,
    }) {
        this._id = id;
        this._exportRatio = exportRatio;
        this._subtitleStyleId = subtitleStyleId;
        await this._videoElement.init(videoUrl, {
            sharedVideoPlayBase: this._sharedVideoPlayBase || undefined,
            isLightVideo: isLightVideo,
        });

        this._duration = this._videoElement.getVideoDuration();
        await this.addSubtitle(subtitles, startTime, language);

        this.setTimeData(startTime, endTime);
        this._currentTime = 0;
        this._videoElement.setPreviewExportRatio(exportRatio);
        this.setFaceDetectionData(faceData);
        this._stableFragments = stableFragments;
        this._videoFps = Number.isFinite(fps) && fps > 0 ? fps : 30;
        this._exportBlob = null;
    }
    async updateData({ startTime, endTime, subtitles, faceData, language }) {
        this.addSubtitle(subtitles, startTime, language);

        this.setTimeData(startTime, endTime);
        this._videoElement.setPreviewExportRatio(this._exportRatio);
        this.setFaceDetectionData(faceData);
        this._currentTime = 0;
        await this.seek(0);
        this._exportBlob = null;
    }
    /**
     * @desc 设置字幕预览视图（与 setVideoCanvas 对应，用于同步渲染字幕）
     * @param {HTMLElement | null} view
     */
    setSubtitleView(view) {
        if (this._subtitleResizeTimer != null) {
            clearTimeout(this._subtitleResizeTimer);
            this._subtitleResizeTimer = null;
        }
        this._subtitleView = view;
        this._lastSubtitleLayoutSize = null;
        this._subtitleHadLayoutOnce = false;
        if (view && this._subtitle.isEmpty()) {
            view.innerHTML = '';
        }
    }

    /**
     * @param {number} time
     * @param {{ flush?: boolean }} [renderOptions] `flush`：先移出再挂回字幕层，强制走 `addToView`→`updateView`（仅在大尺寸跳变时使用，见 resizeView）
     */
    _renderSubtitleAt(time, renderOptions = {}) {
        if (!this._subtitleView) return;
        // 导出时字幕 DOM 挂在 _exportView 上（见 FJSprite.prepareToExport）。
        // 若仍走预览 render，会把 _layer 从导出树挪到 _subtitleView，导致 FJDomRender.getContentDom 为 null（报错 Element dom is null. type:subtitleText）。
        if (FJExportStore.getIsExporting()) return;
        if (this._subtitle.isEmpty()) {
            this._subtitleView.innerHTML = '';
            return;
        }
        try {
            this._subtitle.render(this._subtitleView, time, renderOptions);
        } catch (e) {
            console.warn('字幕渲染失败', e);
        }
    }

    /**
     * @desc 设置时间数据
     * @param {number} startTime
     * @param {number} endTime
     */
    setTimeData(startTime, endTime) {
        this._videoElement.setTimeData(startTime, endTime);
        this._duration = endTime - startTime;
    }
    async addVideoElement(url) {
        await this._videoElement.init(url);
        this._duration = this._videoElement.getVideoDuration();
    }
    /**
     * @desc 清空所有字幕（与 video 独立，可单独调用）
     */
    clearSubtitles() {
        if (this._subtitleResizeTimer != null) {
            clearTimeout(this._subtitleResizeTimer);
            this._subtitleResizeTimer = null;
        }
        this._subtitleHadLayoutOnce = false;
        this._subtitle.clear();
        this._lastSubtitleLayoutSize = null;
        if (this._subtitleView) {
            this._subtitleView.innerHTML = '';
        }
    }

    async addSubtitle(subtitles, startTime, language) {
        if (this._subtitleStyleId === 'None') {
            return;
        }
        if (!subtitles || subtitles.length === 0) return this._subtitle;
        this._subtitle.clear();
        this._lastSubtitleLayoutSize = null;
        this._subtitleHadLayoutOnce = false;
        const list = FJSubtitleUtil.buildSubtitleLines(subtitles, {
            ratioType: this._exportRatio,
            lang: language,
        });
        const subtitleStyleJson = await FJUtil.getSubtitleStyleJson(this._subtitleStyleId);

        list.forEach(item => {
            const { text, offset, duration, wordTimeList, fontSize } = item;
            const start = Math.max(0, Number(offset - startTime) || 0);

            let tempWordTimeList = (wordTimeList || []).map(w => {
                return {
                    startTime: w.offset - startTime,
                    duration: w.duration,
                };
            });
            const el = new FJSubtitleText({ textData: text });
            const json = {
                ...el.toJSON(),
                id: el.getId(),
                ...FJCoreConfig.defaultSubtitleStyle,
                version: 2,
                subtitleTextDelay: start,
                subtitleTextDuration: duration,
                fontSize: fontSize,
            };
            el.wordTimestampList = [...tempWordTimeList];

            el.fromJSON(json);
            el.updateAnimation && el.updateAnimation();
            el.updateRelativeRect();
            this._subtitle.addElement(el);
        });
        if (subtitleStyleJson) {
            await this.updateSubtitlesStyle(subtitleStyleJson);
            this._subtitle.stylePresetId = this._subtitleStyleId;
        }
    }

    async getSubtitlesStyle(jsonUrl) {
        if (!jsonUrl) {
            return;
        }
        const json = await FJNetworkManager.fetchResource(
            `${FJConfig.RESOURCE_SERVER}${jsonUrl}?v=${FJConfig.STATIC_RESOURCE_VERSION}`,
        );
        if (!json) {
            return;
        }
        return json;
    }
    async updateSubtitlesStyle(json) {
        const textInfo = json.info;
        const tempStyle = FJTextStyleManager.createStyle(FJTextStyleManager.TEXT_STYLES[textInfo.style.type]);
        tempStyle.fromJSON(textInfo.style);
        const value = {
            // fontSize: textInfo.fontSize,
            fontFamily: FJCoreConfig.fontFamily,
            background: textInfo.background,
            padding: textInfo.padding,
            alignment: textInfo.alignment,
            spacing: textInfo.spacing,
            lineHeight: textInfo.lineHeight,
            opacity: textInfo.opacity,
            subtitleStyle: {
                type: tempStyle.type,
                info: tempStyle.getAdjustInfo(),
            },
            fontColor: textInfo.fontColor,
            isBold: textInfo.isBold,
            isItalic: textInfo.isItalic,

            animationInName: textInfo.animationInName,
            animationOutName: textInfo.animationOutName,
            animationBackgroundColor: textInfo.animationBackgroundColor,
            animationTextColor: textInfo.animationTextColor,
        };
        const data = { ...value };
        delete data.fontColor;
        delete data.isBold;
        delete data.isItalic;
        delete data.opacity;
        delete data.style;
        this._subtitle.setAttr('textCombinationType', { textCombinationType: 'word' });
        this._subtitle.setAttr('opacity', { opacity: value.opacity });
        this._subtitle.setAttr('', {
            ...data,
            color1: value.fontColor,
            // bold: value.isBold,
            // italic: value.isItalic,
        });
        this._subtitle.setAttr('style', { ...value.subtitleStyle });
    }
    getDuration() {
        return this._duration;
    }

    /** @desc 获取当前时间线时间（由 this._currentTime 统一控制） */
    getCurrentTime() {
        return this._currentTime;
    }

    getVideoElement() {
        return this._videoElement;
    }

    /**
     * @param {unknown[]} data mergeFaceDetectionTimeline 结果：各块时间区间互不重叠；同一段多人脸在同一块的 segments 中（含 frameCount）
     */
    setFaceDetectionData(data) {
        this._videoElement.setFaceDetectionData(Array.isArray(data) ? data : []);
    }
    getSubtitle() {
        return this._subtitle;
    }

    // ---------- 预览/播放控制，直接委托给 videoElement ----------

    /**
     * @desc 设置视频画布
     * @param {HTMLCanvasElement} canvas
     */
    setVideoCanvas(canvas) {
        this._videoElement.setVideoCanvas(canvas);
    }

    /**
     * @param {((loading: boolean) => void) | null} fn
     */
    setBufferingListener(fn) {
        this._videoElement.setBufferingListener(fn);
    }

    /**
     * @desc seek（视频 + 字幕同步，时间由 this._currentTime 统一更新）
     * @param {number} time
     */
    async seek(time) {
        this._currentTime = time;
        await this._videoElement.seek(time);
        this._renderSubtitleAt(this._currentTime);
    }

    /**
     * @desc 暂停（视频 + 字幕同步，暂停时 this._currentTime 在 onPause 中已更新）
     */
    async pause() {
        await this._videoElement.pause();
        this._subtitle.pause();
        this._renderSubtitleAt(this._currentTime);
    }

    /**
     * 被其它卡抢占时仅清当前 manager 的播放状态，不暂停共享底层媒体。
     */
    stopLocalPlaybackState() {
        this._videoElement.stopLocalPlaybackState();
        this._subtitle.pause();
    }

    /**
     * @desc 播放（视频 + 字幕同步，每帧由 callback 更新 this._currentTime）
     * @param {Function} [callback] 每帧回调，参数为时间线当前时间
     * @param {Function} [pauseCallback] 暂停时回调，参数为时间线当前时间
     */
    /**
     * 用户点击播放的瞬间同步调用，配合 `FJVideoElement` 静音预播保留手势以实现带声播放。
     */
    primePlaybackForGesture() {
        this._videoElement.primePlaybackForGesture();
    }

    async play(callback, pauseCallback) {
        const onFrame = time => {
            this._currentTime = time;
            this._renderSubtitleAt(this._currentTime);
            callback && callback(time);
        };
        const onPause = time => {
            this._subtitle.pause();
            // 播放到裁剪片段末尾时，回到时间线起点（0），便于再次播放
            const duration = this._duration;
            const atSegmentEnd = duration > 0 && time >= duration - 1e-2;
            if (atSegmentEnd) {
                void this.seek(0).then(() => {
                    this._currentTime = 0;
                    pauseCallback && pauseCallback(0);
                });
            } else {
                this._currentTime = time;
                this._renderSubtitleAt(this._currentTime);
                pauseCallback && pauseCallback(time);
            }
        };
        await this._videoElement.play(onFrame, onPause);
    }

    /**
     * @desc 绘制视频画布（同时渲染当前帧字幕，使用 this._currentTime）
     */
    drawVideoCanvas() {
        this._videoElement.drawVideoCanvas();
        this._renderSubtitleAt(this._currentTime);
    }

    /**
     * @desc 清除（视频 + 字幕）
     */
    clear() {
        if (this._subtitleResizeTimer != null) {
            clearTimeout(this._subtitleResizeTimer);
            this._subtitleResizeTimer = null;
        }
        this._subtitleHadLayoutOnce = false;
        this._videoElement.clear();
        this._subtitle.clear();
        this._currentTime = 0;
        this._lastSubtitleLayoutSize = null;
        if (this._subtitleView) {
            this._subtitleView.innerHTML = '';
        }
    }

    /**
     * @returns {{ width: number, height: number } | null} 与 **预览画布** 同一套 CSS 像素（必须与 `FJVideoElement.initVideoRect` 读的 canvas 同源，否则字幕相对坐标会与视频偏移）
     */
    _getSubtitleViewSize() {
        if (!this._subtitleView) return null;
        const canvas =
            this._subtitleView.previousElementSibling?.tagName === 'CANVAS'
                ? this._subtitleView.previousElementSibling
                : this._subtitleView.parentElement?.querySelector?.('canvas');
        const dom = canvas ?? this._subtitleView;
        const r = dom.getBoundingClientRect();
        const w = r.width;
        const h = r.height;
        if (!Number.isFinite(w) || !Number.isFinite(h) || w < 1 || h < 1) return null;
        return { width: w, height: h };
    }

    /**
     * 宽高相对上一帧变化超过阈值则 flush 字幕（全屏等）；渐进式窗口缩放只 updateRelativeRect，避免卡顿。
     * @param {{ width: number, height: number }} vs
     * @returns {boolean}
     */
    _shouldFlushSubtitleLayout(vs) {
        const last = this._lastSubtitleLayoutSize;
        if (!last) {
            this._lastSubtitleLayoutSize = { width: vs.width, height: vs.height };
            return false;
        }
        const lw = Math.max(last.width, 1);
        const lh = Math.max(last.height, 1);
        const dw = Math.abs(vs.width - last.width) / lw;
        const dh = Math.abs(vs.height - last.height) / lh;
        this._lastSubtitleLayoutSize = { width: vs.width, height: vs.height };
        return dw > 0.1 || dh > 0.1;
    }

    /**
     * 仅同步字幕几何与 DOM（由防抖回调或尺寸重试调用）；视频已在 resizeView 中更新。
     */
    _syncSubtitleLayoutToContainer() {
        if (FJExportStore.getIsExporting() || !this._subtitleView) return;
        const vs = this._getSubtitleViewSize();
        const flush = !!(vs && this._shouldFlushSubtitleLayout(vs));
        if (vs) {
            this._subtitle.updateRelativeRect(vs, false);
        }
        this._renderSubtitleAt(this._currentTime, flush ? { flush: true } : {});
        if (!vs && !this._pendingSubtitleResizeRetry) {
            this._pendingSubtitleResizeRetry = true;
            const tryLater = left => {
                requestAnimationFrame(() => {
                    if (this._getSubtitleViewSize()) {
                        this._pendingSubtitleResizeRetry = false;
                        this._syncSubtitleLayoutToContainer();
                    } else if (left > 0) {
                        tryLater(left - 1);
                    } else {
                        this._pendingSubtitleResizeRetry = false;
                    }
                });
            };
            tryLater(5);
        }
    }

    _scheduleSubtitleLayoutAfterResize() {
        if (FJExportStore.getIsExporting() || !this._subtitleView) return;
        const vsPeek = this._getSubtitleViewSize();
        if (!vsPeek) return;

        if (!this._subtitleHadLayoutOnce) {
            this._subtitleHadLayoutOnce = true;
        }
        if (this._subtitleResizeTimer != null) {
            clearTimeout(this._subtitleResizeTimer);
            this._subtitleResizeTimer = null;
        }
        /** 任意一次 resize：`initVideoRect` 已读 canvas bbox；字幕须同帧/次帧同步几何，只靠 140ms 防抖会在全屏退出后短时仍按旧视口排版。 */
        requestAnimationFrame(() => {
            this._syncSubtitleLayoutToContainer();
        });
        this._subtitleResizeTimer = setTimeout(() => {
            this._subtitleResizeTimer = null;
            this._syncSubtitleLayoutToContainer();
        }, SUBTITLE_LAYOUT_DEBOUNCE_MS);
    }

    /**
     * @desc 视口尺寸变化时重新计算画布并重绘。**字幕**：每帧先 `rAF` 同步几何，再给 140ms 防抖收口（拖拽窗口时减抖）；全屏进/退仅依赖防抖会落后于视频一拍。
     */
    resizeView() {
        this._videoElement.initVideoRect();
        this._videoElement.drawVideoCanvas();
        this._scheduleSubtitleLayoutAfterResize();
    }
    updateView() {
        this._renderSubtitleAt(this._currentTime);
    }
    _getExportSize(ratioKey, resolution = 1080, sourceWidth, sourceHeight) {
        if (
            ratioKey === 'original' &&
            sourceWidth != null &&
            sourceHeight != null &&
            sourceWidth > 0 &&
            sourceHeight > 0
        ) {
            return { width: sourceWidth, height: sourceHeight };
        }
        const parse = s => {
            const [a, b] = s.split(':').map(Number);
            return { w: a, h: b };
        };
        const safeKey = ratioKey == null ? '' : String(ratioKey);
        const r = safeKey.includes(':') ? parse(safeKey) : { w: 1, h: 1 };
        const isPortrait = r.w < r.h;
        if (isPortrait) {
            return { width: Math.round((resolution * r.w) / r.h), height: resolution };
        }
        return { width: resolution, height: Math.round((resolution * r.h) / r.w) };
    }

    /**
     * 当前片段首帧封面（与导出比例一致，复用 FJVideoElement.getCoverThumbnail 构图逻辑）
     * @param {{ resolution?: number, mimeType?: string, quality?: number }} [options] resolution 为长边像素，默认 480；mimeType 默认 image/jpeg
     * @returns {Promise<Blob | null>}
     */
    async getCoverThumbnailBlob(options = {}) {
        const { resolution = 480, mimeType = 'image/jpeg', quality } = options;
        const { width: sw, height: sh } = this._videoElement.getSourceInfo();
        if (!sw || !sh || sw <= 0 || sh <= 0) {
            return null;
        }
        const ratioKey =
            this._exportRatio === 'original' ||
            (typeof this._exportRatio === 'string' && this._exportRatio.includes(':'))
                ? this._exportRatio
                : '9:16';
        const { width, height } = this._getExportSize(ratioKey, resolution, sw, sh);
        return this._videoElement.getCoverThumbnail({
            width,
            height,
            mimeType,
            quality,
        });
    }

    /**
     * 人脸分析入口（须经 `FJFaceDetectScheduler`，全局同一时间最多 N 个任务，N 见 `FJCoreConfig.FACE_DETECT_MAX_CONCURRENT`）
     * @param {Object} [options]
     * @param {() => void} [options.onProgress]
     * @param {(p: { segmentSec: number, decodeDetectMs: number }) => void} [options.onTiming]
     */
    async startDetectFace(options = {}) {
        return FJFaceDetectScheduler.run(() => this._startDetectFaceImpl(options));
    }

    /**
     * @param {Object} [options]
     * @param {() => void} [options.onProgress]
     * @param {(p: { segmentSec: number, decodeDetectMs: number }) => void} [options.onTiming]
     */
    async _startDetectFaceImpl(options = {}) {
        const { onProgress = () => {}, onTiming } = options;
        const { width, height } = this._videoElement.getSourceInfo();
        const { startTime, endTime } = this._videoElement.getTimeData();
        const file = await this._videoElement.getSourceBlob();
        if (
            !file ||
            !width ||
            !height ||
            width <= 0 ||
            height <= 0 ||
            !Number.isFinite(startTime) ||
            !Number.isFinite(endTime) ||
            endTime <= startTime
        ) {
            return;
        }

        const { width: decodeWidth, height: decodeHeight } = FJMathUtil.scaleToMaxLongEdge(width, height, 640);
        const detectOpts = {
            startTime,
            endTime,
            width: decodeWidth,
            height: decodeHeight,
            fps: this._videoFps,
            stableFragments: this._stableFragments,
            onProgress: p => {
                onProgress(p);
            },
            onTiming,
        };
        const detectStore =
            Array.isArray(this._stableFragments) && this._stableFragments.length > 0
                ? FJFragmentDetectStore
                : FJDetectStore;
        const faceDataBlocks = await detectStore.decodeAndDetect(file, detectOpts);
        this.setFaceDetectionData(faceDataBlocks);
        return faceDataBlocks;
    }
    export(options = {}) {
        const { onProgress, onComplete, onError } = options;
        if (this._exportBlob) {
            onComplete(this._exportBlob);
            return;
        }
        const { width, height } = this._videoElement.getSourceInfo();
        const { startTime, endTime } = this._videoElement.getTimeData();

        const exportSize = this._getExportSize(this._exportRatio, 1080, width, height);

        FJExportStore.stopExport();
        FJExportStore.export({
            detailManager: this,
            startTime,
            endTime,
            exportSize,
            onProgress: p => onProgress(p),
            onComplete: blob => {
                FJExportStore.stopExport();
                if (blob) {
                    this._exportBlob = blob;
                    onComplete(blob);
                }
            },
            onError: err => {
                FJExportStore.stopExport();
                this._exportBlob = null;
                onError(err);
            },
        });
    }
    /**
     * 导出当前片段时间线到 mp4（`FJExportStore` 编码进度 0～1 经 `onProgress` 透出）。
     * 串行批量场景由 `FJProjectStore.runExportSelfSerial` 再映射为总进度；落盘见 `saveExportedBlob`。
     * @param {{ onProgress?: (p: number) => void }} [options]
     * @returns {Promise<Blob | null>} 成功为 Blob，失败或中断为 null
     */
    async exportSelf(options = {}) {
        if (this._exportBlob) {
            return this._exportBlob;
        }
        const { onProgress } = options;
        const { width, height } = this._videoElement.getSourceInfo();
        const { startTime, endTime } = this._videoElement.getTimeData();

        const exportSize = this._getExportSize(this._exportRatio, 1080, width, height);

        FJExportStore.stopExport();
        return await new Promise(resolve => {
            FJExportStore.export({
                detailManager: this,
                startTime,
                endTime,
                exportSize,
                onProgress: p => onProgress?.(p),
                onComplete: blob => {
                    FJExportStore.stopExport();
                    this._exportBlob = blob;
                    resolve(blob ?? null);
                },
                onError: () => {
                    FJExportStore.stopExport();
                    this._exportBlob = null;
                    resolve(null);
                },
            });
        });
    }
    async getEditAppTimeData(videoMeta) {
        const timelineRenderCache = this.getVideoElement().getTimelineRenderCache();
        const { duration, width, height } = this._videoElement.getSourceInfo();
        const blob = await this._videoElement.getSourceBlob();
        const isLightVideo = this._videoElement.getIsLightVideo();
        const size = blob.size;
        const fileKeys = await FJVideoUtil.saveFileListToLocalStorage([{ file: blob }]);
        const fileMeta = {
            id: fileKeys[0].key,
            type: 'VIDEO',
            mimeType: 'video/mp4',
            width: width,
            height: height,
            name: videoMeta?.title,
            duration,
            size: size,
            isLightVideo: isLightVideo,
        };
        const pid = FJArchiveUtil.createId(true);
        const editAppBlocksData = FJEditAppUtil.buildEditAppBlocksData(timelineRenderCache, fileMeta, pid);
        const subtitleJson = this._subtitle.toJSON();
        const projectData = FJEditAppUtil.buildProjectData(editAppBlocksData, subtitleJson, fileMeta, pid);
        return projectData;
    }
}
export default FJDetailManager;
